import { LabTestResult } from '../lab-test-results';
import { Selection, select } from 'd3';
import { CvdChartData, BoxplotData, CvdChartDataValue } from '@covance/ng-cvd3';
import { LabTrendsChartDataValue } from './trends-chart-data-builder';
import { ChartLegendItem } from '../legend';
import { isEmpty, chain } from 'lodash';

const FEMALE_COLOR = '#F06292'; 
const MALE_COLOR = '#4FC3F7';
const DEFAULT_COLOR = '#609EC9';

export function getShapeByGender(gender: string) {
    switch ((gender || '').toLowerCase()) {
        case 'male':
        case 'm':
            return 'triangle-down';
        case 'female':
        case 'f':
            return 'triangle-up';
        default:
            return 'circle';
    }
}

export function getColorByGender(gender: string) {
    switch ((gender || '').toLowerCase()) {
    case 'male':
    case 'm':
            return MALE_COLOR;
    case 'female':
    case 'f':
            return FEMALE_COLOR;
    default:
            return DEFAULT_COLOR;
    }
}

export function getSexLabelByShape(shape: string) {
    switch (shape) {
        case 'triangle-up':
            return 'female';
        case 'triangle-down':
            return 'male';
        default:
            return 'unknown';
    }
}

export class FlaggedClassNames {
    public static readonly high = 'flagged-high';
    public static readonly low = 'flagged-low';
    public static readonly other = 'flagged-other';
    public static readonly nonFlagged = 'non-flagged';
}

export function getFlaggedClassName(labTestResult: LabTestResult): string {
    if (!labTestResult.isFlagged) {
        return FlaggedClassNames.nonFlagged;
    }

    if (labTestResult.alertFlag) {
        const alertFlag = labTestResult.alertFlag.toUpperCase();

        if (alertFlag.startsWith('H')) {
            return FlaggedClassNames.high;
        }

        if (alertFlag.startsWith('L')) {
            return FlaggedClassNames.low;
        }
    }

    return FlaggedClassNames.other;
}

export function canBeUsedInCharts(r: LabTestResult) {
    return !r.isCancelled && 
           r.currentResult && 
           r.currentResult.numericResult !== null &&
           !r.isQuestion;
}

export function canBeUsedInTrends(r: LabTestResult) {
    return canBeUsedInCharts(r) && r.visitCollectionDate;
}

const pointsSelector = '.cv-scatter g.cv-groups g.cv-group path.cv-point';
const linesSelector = '.cv-wrap g.cv-groups g.cv-group path.cv-line';
const opaqueClassName = 'opaque';
const hiddienClassName = 'cvdhidden';

function classedAllLineChartElements(svgChart: Selection<SVGElement, CvdChartData<LabTrendsChartDataValue>[], HTMLElement, any>, className: string, onOfSwitch: boolean) {
    [linesSelector, pointsSelector]
        .forEach(selector => {
            svgChart.selectAll<SVGPathElement, LabTrendsChartDataValue>(selector)
            .classed(className, onOfSwitch);
        });
}

function classedSelectedLineChartElements(svgChart: Selection<SVGElement, CvdChartData<LabTrendsChartDataValue>[], HTMLElement, any>, className: string, selectedRows: number[]) {
    svgChart.selectAll<SVGPathElement, LabTrendsChartDataValue[]>(linesSelector)
            .classed(className, (d) =>  {
                return !d.length || selectedRows.indexOf(d[0].rows[0]) !== -1;
            });

    svgChart.selectAll<SVGPathElement, LabTrendsChartDataValue>(pointsSelector)
            .classed(className, (d) =>  selectedRows.indexOf(d.rows[0]) !== -1);
}

function removeClassFromAllLineChartElements(svgChart: Selection<SVGElement, CvdChartData<LabTrendsChartDataValue>[], HTMLElement, any>, className: string) {
    classedAllLineChartElements(svgChart, className, false);
}

export function hideAllButSelectedSubjects(svgChart: Selection<SVGElement, CvdChartData<LabTrendsChartDataValue>[], HTMLElement, any>, selectedItems: ChartLegendItem[], data: LabTestResult[]) {
    if (isEmpty(selectedItems)) {
        removeClassFromAllLineChartElements(svgChart, hiddienClassName);
        return;
    }
    const selectedSubjectsIds = selectedItems.map(i => i.key);
    const selectedTests = data
        .filter(test => selectedSubjectsIds.indexOf(test.ctSubjectId) === -1)
        .map(e => e.labTestId);
    
    classedSelectedLineChartElements(svgChart, hiddienClassName, selectedTests);
}

export function opaqueAllButSelectedSubjects(svgChart: Selection<SVGElement, CvdChartData<LabTrendsChartDataValue>[], HTMLElement, any>, selectedItem: ChartLegendItem, data: LabTestResult[]) {
    const selectedTests = data.filter(p => p.ctSubjectId !== selectedItem.key).map(e => e.labTestId);
    classedSelectedLineChartElements(svgChart, opaqueClassName, selectedTests);
}

export function removeOpacityFromAllElements(svgChart: Selection<SVGElement, CvdChartData<LabTrendsChartDataValue>[], HTMLElement, any>) {
    removeClassFromAllLineChartElements(svgChart, opaqueClassName);
}

export function getOutliers() {
    return select('.cvd3-chart').selectAll('.cv-outlier');
}

export function clearOutliers() {
    getOutliers().remove();
}

export function buildBoxPlotGenderLegend(data: CvdChartData<CvdChartDataValue>[], isGendersSplited: boolean) {
    if (isEmpty(data)) {
        return [];
    }

    let genders: string[];
    if (isGendersSplited) {
        genders = data.map(d => d.key);
    } else {
        const outliers = getOutliers().data() as BoxplotData[];
        genders = chain(outliers)
            .map(dataPoint => dataPoint.shape)
            .uniq()
            .map(shape => getSexLabelByShape(shape))
            .value();
    }
    return chain(genders).map(g => ({
                shape: getShapeByGender(g),
                color: isGendersSplited ? getColorByGender(g) : getColorByGender(''),
                label: g,
                key: null
            }))
            .orderBy(l => l.label.toLowerCase())
            .value();
}

export function boxPlotVisitTickRenderrer(groupKey: string, results: LabTestResult[]) {
    if (isEmpty(results)) {
        return '';
    }

    var result = results.find(r => r.visitCode === groupKey);
    return result && result.visitName || '';
}

export function boxPlotSiteTickRenderrer(groupKey: string, results: LabTestResult[]) {
    if (isEmpty(results)) {
        return '';
    }

    var result = results.find(r => r.siteNumber === groupKey);
    return result && result.siteNumber || '';
}